/* eslint-disable camelcase */
import React, { useState, useEffect } from "react"
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  Typography,
  Box,
  IconButton,
  Chip,
} from "@mui/material"
import { DataGrid } from "@mui/x-data-grid"
import CloseIcon from "@mui/icons-material/Close"
import AddTaskIcon from "@mui/icons-material/AddTask"
import { useQuery } from "@tanstack/react-query"
import { api } from "../../../../api"
import normalFormatDate from "../../../utils/date/DDMMYYYFormatDate"
import Swal from "sweetalert2"

// Helper monetario
const formatMoneda = (valor) => {
  return new Intl.NumberFormat("en-US", { style: "currency", currency: "USD" }).format(valor || 0)
}

const ModalBuscarDeudas = ({ open, onClose, clicodigo, onAddDeudas }) => {
  const [selectionModel, setSelectionModel] = useState([])

  // Fetch de deudas pendientes al abrir el modal con un cliente válido
  const { data, isFetching } = useQuery({
    queryKey: ["deudasCliente", clicodigo],
    queryFn: async () => {
      const res = await api.post("/CobrosVista/getDeudasCliente", {
        page: 1,
        perPage: 500, // Carga masiva para el modal de selección en memoria
        externalFilters: { clicodigo },
      })

      // =========================================================================
      // EXTRACCIÓN DEFENSIVA (Aplicando tu estándar)
      // =========================================================================
      const rawData = res?.data?.data || res?.data || res || []
      const listaValidada = Array.isArray(rawData) ? rawData : Array.isArray(rawData?.data) ? rawData.data : []

      return listaValidada
    },
    enabled: open && !!clicodigo,
    refetchOnWindowFocus: false,
  })

  const rows = data || []

  // Reiniciar selección cada vez que se abre el modal
  useEffect(() => {
    if (open) setSelectionModel([])
  }, [open])

  const handleAgregar = () => {
    if (selectionModel.length === 0) {
      Swal.fire("Aviso", "Debe seleccionar al menos una cuota para cobrar.", "warning")
      return
    }

    // Identificamos las filas completas a partir del selectionModel
    const selectedRows = rows.filter((r) => selectionModel.includes(`${r.facnumfac}-${r.facsecuen}`))

    // =========================================================================
    // REGLA DE NEGOCIO (VB6 - bFlagHueco):
    // Las cuotas deben seleccionarse en orden secuencial sin dejar huecos.
    // =========================================================================
    let bFlagHueco = false
    for (let i = 0; i < rows.length; i++) {
      const rowId = `${rows[i].facnumfac}-${rows[i].facsecuen}`
      const isSelected = selectionModel.includes(rowId)

      if (isSelected) {
        if (i === 0) continue // El primer registro de la tabla es siempre válido

        // Verificamos si la cuota inmediatamente anterior NO está seleccionada
        const prevRowId = `${rows[i - 1].facnumfac}-${rows[i - 1].facsecuen}`
        if (!selectionModel.includes(prevRowId)) {
          bFlagHueco = true
          break
        }
      }
    }

    if (bFlagHueco) {
      Swal.fire(
        "Selección Inválida",
        "Debe seleccionar las cuotas a cobrar en orden secuencial (desde la más antigua), sin dejar huecos.",
        "error",
      )
      return
    }

    // Formatear objeto para inyectar en la planilla principal (TablaPlanillaCobro)
    const deudasAEnviar = selectedRows.map((row) => ({
      ...row,
      abono: row.saldo_neto, // Sugerimos el abono igual al saldo neto por defecto
      cobrar_mora: row.lDiasVen > 0, // Flag para que el motor sepa si debe crear ND Mora
    }))

    onAddDeudas(deudasAEnviar)
    onClose()
  }

  const columns = [
    {
      field: "facnumfac",
      headerName: "Documento",
      width: 160,
      renderCell: (params) => (
        <Typography variant="body2" fontWeight="bold" color="primary.main">
          {params.value}
        </Typography>
      ),
    },
    { field: "facsecuen", headerName: "Cuota", width: 70, align: "center", headerAlign: "center" },
    { field: "factipo", headerName: "Tipo", width: 70, align: "center", headerAlign: "center" },
    {
      field: "facfecemi",
      headerName: "Emisión",
      width: 100,
      renderCell: (params) => normalFormatDate(params.value),
    },
    {
      field: "facfecven",
      headerName: "Vencimiento",
      width: 100,
      renderCell: (params) => normalFormatDate(params.value),
    },
    {
      field: "lDiasVen",
      headerName: "Días Mora",
      width: 90,
      align: "center",
      headerAlign: "center",
      renderCell: (params) => (
        <Chip
          label={params.value || 0}
          size="small"
          color={params.value > 0 ? "error" : "default"}
          variant={params.value > 0 ? "filled" : "outlined"}
        />
      ),
    },
    {
      field: "saldo_neto",
      headerName: "Saldo Capital",
      width: 120,
      align: "right",
      headerAlign: "right",
      renderCell: (params) => formatMoneda(params.value),
    },
    {
      field: "mora_calculada",
      headerName: "Interés Mora",
      width: 110,
      align: "right",
      headerAlign: "right",
      renderCell: (params) => (
        <Typography variant="body2" color="error.main" fontWeight={params.value > 0 ? "bold" : "normal"}>
          {formatMoneda(params.value)}
        </Typography>
      ),
    },
    {
      field: "total_cobrar_sugerido",
      headerName: "Total Sugerido",
      width: 130,
      align: "right",
      headerAlign: "right",
      renderCell: (params) => (
        <Typography variant="body2" fontWeight="bold">
          {formatMoneda(params.value)}
        </Typography>
      ),
    },
  ]

  return (
    <Dialog open={open} onClose={onClose} maxWidth="lg" fullWidth>
      <DialogTitle sx={{ m: 0, p: 2, bgcolor: "#f5f7fa", color: "#196C87" }}>
        <Box display="flex" alignItems="center" justifyContent="space-between">
          <Typography variant="h6" fontWeight="bold">
            Selección de Cartera Pendiente
          </Typography>
          <IconButton onClick={onClose} size="small">
            <CloseIcon />
          </IconButton>
        </Box>
      </DialogTitle>

      <DialogContent dividers sx={{ p: 2, height: "60vh" }}>
        <Typography variant="body2" color="textSecondary" mb={2}>
          Seleccione las cuotas que el cliente desea cancelar. Recuerde que el sistema exige el cobro en orden
          cronológico.
        </Typography>

        <DataGrid
          rows={rows}
          columns={columns}
          getRowId={(row) => `${row.facnumfac}-${row.facsecuen}`}
          loading={isFetching}
          checkboxSelection
          disableRowSelectionOnClick
          rowSelectionModel={selectionModel}
          onRowSelectionModelChange={(newSelection) => {
            setSelectionModel(newSelection)
          }}
          sx={{
            "& .MuiDataGrid-cell": { fontSize: "0.85rem" },
            "& .MuiDataGrid-columnHeaders": { backgroundColor: "#f5f5f5", fontWeight: "bold" },
          }}
          hideFooterPagination
        />
      </DialogContent>

      <DialogActions sx={{ p: 2, bgcolor: "#f5f7fa" }}>
        <Button onClick={onClose} color="inherit" variant="outlined" sx={{ fontWeight: "bold" }}>
          Cancelar
        </Button>
        <Button
          onClick={handleAgregar}
          color="primary"
          variant="contained"
          startIcon={<AddTaskIcon />}
          sx={{ fontWeight: "bold" }}
        >
          Agregar Seleccionadas
        </Button>
      </DialogActions>
    </Dialog>
  )
}

export default ModalBuscarDeudas
